import React from "react";
import { useIntl } from "react-intl";
import { PeekBox, PeekChip, PeekLink, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import Award from "@piiqtechnologies/svg-icons/lib/Award";
import ExternalLink from "@piiqtechnologies/svg-icons/lib/ExternalLink";
import messages from "./messages.i18n";
export const RankBadge = ({ label, size = "medium" }) => {
    const theme = useTheme();
    return (React.createElement(PeekChip, { label: label, size: size, sx: {
            height: "100%",
            borderRadius: 1,
            backgroundColor: "transparent",
            color: theme.palette.grey[400],
            fontWeight: theme.typography.fontWeightLight,
            minWidth: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            "&.MuiChip-root": {
                backgroundColor: "transparent",
            },
            "& .MuiChip-label": {
                px: 1,
                pt: 1,
                fontSize: theme.typography.h3.fontSize,
                height: "100%",
                fontWeight: theme.typography.fontWeightLight,
            },
        } }));
};
export const MedalIcon = ({ "aria-label": ariaLabel }) => {
    const theme = useTheme();
    return (React.createElement(PeekBox, { component: "span", "aria-label": ariaLabel, sx: {
            fontSize: "1.25rem",
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            "& svg path:first-of-type": { fill: theme.palette.orange[500] },
        } },
        React.createElement(Award, { width: 20, height: 20, style: { color: theme.palette.orange[500] } })));
};
export const ScorePill = ({ score, size = "medium" }) => {
    const intl = useIntl();
    const theme = useTheme();
    const hasScore = score != null && score > 0;
    return (React.createElement(PeekChip, { label: hasScore ? `${Math.round(score)}` : intl.formatMessage(messages.scoreNotAvailable), size: size, sx: {
            height: 26,
            borderRadius: 30,
            "&.MuiChip-filledDefault": {
                backgroundColor: hasScore ? theme.palette.orange[500] : theme.palette.grey[300],
            },
            color: theme.palette.common.white,
            border: "none",
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.body1.fontSize,
            "& .MuiChip-label": {
                px: 1.5,
            },
        } }));
};
const SourceName = ({ source, urls }) => {
    var _a;
    const href = ((_a = urls === null || urls === void 0 ? void 0 : urls[0]) === null || _a === void 0 ? void 0 : _a.url) || (source ? `https://${source}` : null);
    if (href) {
        return (React.createElement(PeekLink, { href: href, target: "_blank", rel: "noopener noreferrer", sx: { display: "inline-flex", alignItems: "center", gap: 0.5 } },
            source,
            React.createElement(ExternalLink, { width: 12, height: 12 })));
    }
    return React.createElement(React.Fragment, null, source);
};
export const CompetitorRow = ({ competitor, rank }) => {
    const intl = useIntl();
    const { bestPerformingSubcategories = [], mostCitedBy = [] } = competitor;
    const bestPerformingText = bestPerformingSubcategories
        .slice(0, 3)
        .map((s) => s.subcategory)
        .join(", ");
    const topCitedSources = mostCitedBy.slice(0, 3);
    return (React.createElement(PeekBox, { component: "li" },
        React.createElement(PeekStack, { direction: "row", spacing: 2, alignItems: "stretch" },
            React.createElement(PeekBox, { sx: { display: "flex", alignItems: "center", minWidth: 80 } },
                React.createElement(RankBadge, { label: `#${rank}`, size: "small" })),
            React.createElement(PeekBox, { sx: { flexGrow: 1 } },
                React.createElement(PeekStack, { spacing: 1 },
                    React.createElement(PeekStack, { direction: "row", alignItems: "center", spacing: 1.5 },
                        rank === 1 && competitor.subjectId && (React.createElement(MedalIcon, { "aria-label": intl.formatMessage(messages.topRankedAriaLabel) })),
                        React.createElement(PeekTypography, { variant: "h4", fontWeight: "fontWeightMedium", sx: { flexGrow: 1 } }, competitor.subjectName),
                        competitor.isSelf && (React.createElement(PeekChip, { label: intl.formatMessage(messages.youBadgeLabel), size: "small", variant: "outlined", color: "secondary", sx: { fontWeight: "medium" } })),
                        React.createElement(ScorePill, { score: competitor.appearanceRate, size: "small" })),
                    React.createElement(PeekStack, { spacing: 0.5 },
                        bestPerformingText && (React.createElement(PeekTypography, { variant: "body1", color: "text.primary" }, intl.formatMessage(messages.bestPerforming, { features: bestPerformingText }))),
                        topCitedSources.length > 0 && (React.createElement(PeekTypography, { variant: "body1", color: "text.primary" },
                            intl.formatMessage(messages.mostCitedBy),
                            " ",
                            topCitedSources.map((s, i, arr) => (React.createElement(React.Fragment, { key: s.source },
                                React.createElement(SourceName, { source: s.source, urls: s.urls }),
                                i < arr.length - 1 && ", ")))))))))));
};
